import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const NAV_LINKS = [
  { label: 'Inicio', href: '/' },
  { label: 'El Servicio', href: '/#servicio' },
  { label: 'Indicadores', href: '/#indicadores' },
  { label: 'Aviso de incidencias', href: '/#incidencias' },
  { label: 'Contacto', href: '/#contacto' },
];

export default function GlassNavbar({ onAccessClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAccessClick = (e) => {
    e.preventDefault();
    if (onAccessClick) {
      onAccessClick();
    } else {
      const el = document.getElementById('acceso');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="top-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-nav' : 'bg-transparent'
      }`}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo + Co-branding */}
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-1"
              aria-label="Cuidemos Los Alcázares — Inicio"
            >
              <span
                className="font-display text-2xl sm:text-3xl tracking-tight"
                style={{ color: scrolled ? '#0A2540' : '#0A2540', lineHeight: 1 }}
              >
                Cuidemos Los Alcázares
                <sup className="text-base align-super" style={{ fontSize: '0.55em' }}>®</sup>
              </span>
            </Link>

            {/* Co-branding logos */}
            <div className="hidden sm:flex items-center gap-2 ml-2 pl-4 border-l border-[rgba(10,37,64,0.15)]">
              {/* Ayuntamiento logo — sin fondo, logo negro visible sobre navbar blanca */}
              <div
                className="h-8 px-1 flex items-center justify-center overflow-hidden"
                title="Ayuntamiento de Los Alcázares"
              >
                <img
                  src="/ayuntamiento-logo.png"
                  alt="Ayuntamiento de Los Alcázares"
                  className="h-7 w-auto object-contain"
                />
              </div>
              {/* Valoriza logo — fondo oscuro necesario (versión negativa = texto blanco) */}
              <div
                className="h-8 px-2 rounded-md bg-[#0A2540] flex items-center justify-center overflow-hidden"
                title="Valoriza Servicios Medioambientales"
              >
                <img
                  src="/valoriza-logo.svg"
                  alt="Valoriza Servicios Medioambientales"
                  className="h-5 w-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1" aria-label="Navegación principal">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`px-3 py-2 rounded-lg text-sm transition-colors duration-150 ${
                  location.pathname === link.href
                    ? 'text-[#0A2540] font-medium'
                    : 'text-[#6F8AA8] hover:text-[#0A2540] hover:bg-[rgba(207,227,245,0.4)]'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-3">
            {user && (
              <span className="hidden sm:block text-xs text-[#6F8AA8] font-medium">
                {user.name}
              </span>
            )}
            <button
              data-testid="top-nav-access-button"
              onClick={handleAccessClick}
              className="rounded-full px-5 py-2 text-sm font-medium bg-[#0A2540] text-white hover:scale-[1.03] transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E5AA8] focus-visible:ring-offset-2"
              aria-label="Acceder al portal"
            >
              Acceder
            </button>
            {/* Mobile menu toggle */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-[rgba(207,227,245,0.5)] transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={20} className="text-[#0A2540]" /> : <Menu size={20} className="text-[#0A2540]" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden pb-4 border-t border-[rgba(10,37,64,0.08)] mt-1">
            <nav className="flex flex-col gap-1 pt-3" aria-label="Navegación móvil">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="px-4 py-3 rounded-lg text-sm text-[#6F8AA8] hover:text-[#0A2540] hover:bg-[rgba(207,227,245,0.4)] transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
